import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import AutomationRuleEditor from './AutomationRuleEditor';

interface AutomationRuleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  ruleId?: number | null;
  initialTemplate?: {
    name: string;
    description?: string;
    trigger_event: string;
    conditions: any[];
    actions: any[];
  } | null;
}

/**
 * 2026-08-26: a right-side panel (Sheet), not a centered modal — stakeholder
 * feedback called the old centered Dialog a "square popup box" and asked for
 * a right-side surface instead, since the Flow canvas (AutomationFlowEditor,
 * now with a component palette alongside it) needs real width to be usable.
 * A Sheet keeps exactly the thing the 2026-08-09 modal restoration was for
 * (a real "back to all rules" close affordance — Radix's SheetContent still
 * renders its own X + closes on overlay click/Escape) while docking to the
 * right and getting most of the viewport width instead of a fixed centered
 * box.
 */
const AutomationRuleDialog: React.FC<AutomationRuleDialogProps> = ({
  open,
  onClose,
  onSave,
  ruleId,
  initialTemplate,
}) => {
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full max-w-none flex-col gap-0 overflow-hidden p-0 sm:w-[92vw] sm:max-w-6xl"
      >
        <SheetHeader className="shrink-0 border-b border-border px-6 py-3.5 pr-14 text-left">
          <SheetTitle>{ruleId ? 'Edit Automation Rule' : 'Create Automation Rule'}</SheetTitle>
        </SheetHeader>
        {/* Same scroll-fix pattern as everywhere else in this app: h-0
            min-h-0 flex-1 forces a definite height so overflow-y-auto
            always has something concrete to scroll within. */}
        <div className="h-0 min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {open && (
            <AutomationRuleEditor
              key={ruleId ?? initialTemplate?.name ?? 'new'}
              ruleId={ruleId ?? null}
              initialTemplate={initialTemplate ?? null}
              onSaved={() => {
                onSave();
                onClose();
              }}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AutomationRuleDialog;
